interface FormattedMessageProps {
  content: string;
}

export function FormattedMessage({ content }: FormattedMessageProps) {
  // Remove action plan markers if present (they're shown separately)
  const cleanContent = content.replace(/\[ACTION_PLAN_START\][\s\S]*?\[ACTION_PLAN_END\]/g, '').trim();

  if (!cleanContent) return null;

  // Split content into sections
  const sections = cleanContent.split('\n\n').filter(s => s.trim());

  return (
    <div className="space-y-4 text-base">
      {sections.map((section, sectionIndex) => {
        const trimmed = section.trim();

        // Check if it's a numbered list
        const numberedListMatch = trimmed.match(/^(\d+\.\s+.+(\n\d+\.\s+.+)*)/m);
        if (numberedListMatch) {
          const items = trimmed.split('\n').filter(line => /^\d+\.\s+/.test(line));
          return (
            <ol key={sectionIndex} className="list-decimal list-outside ml-6 space-y-2.5">
              {items.map((item, idx) => (
                <li key={idx} className="pl-2 leading-relaxed text-gray-700">
                  {item.replace(/^\d+\.\s+/, '')}
                </li>
              ))}
            </ol>
          );
        }

        // Check if it's a bullet list
        const bulletListMatch = trimmed.match(/^[-*•]\s+/m);
        if (bulletListMatch) {
          const items = trimmed.split('\n').filter(line => /^[-*•]\s+/.test(line));
          return (
            <ul key={sectionIndex} className="list-disc list-outside ml-6 space-y-2.5">
              {items.map((item, idx) => (
                <li key={idx} className="pl-2 leading-relaxed text-gray-700">
                  {item.replace(/^[-*•]\s+/, '')}
                </li>
              ))}
            </ul>
          );
        }

        // Check if it's a question (ends with ?)
        if (trimmed.endsWith('?')) {
          return (
            <p key={sectionIndex} className="leading-relaxed font-semibold text-gray-900">
              {trimmed}
            </p>
          );
        }

        // Check if section has multiple lines without being a list
        const lines = trimmed.split('\n').filter(l => l.trim());
        if (lines.length > 1 && !numberedListMatch && !bulletListMatch) {
          return (
            <div key={sectionIndex} className="space-y-2.5">
              {lines.map((line, idx) => (
                <p key={idx} className="leading-relaxed text-gray-700">
                  {line.trim()}
                </p>
              ))}
            </div>
          );
        }

        // Regular paragraph
        return (
          <p key={sectionIndex} className="leading-relaxed text-gray-700">
            {trimmed}
          </p>
        );
      })}
    </div>
  );
}
